import { Component, Mixins, Watch } from "vue-property-decorator";
import { $t } from "@/i18n";
import LabelWithHelp from "@/components/label-with-help";
import ListDropdown from "@/components/list-dropdown";
import { DynPanelAware } from '@/components/dyn-panel';
import Icon from '@/components//icon';
import PanelLocMap from '../../components/panel-loc-map';
import PanelAddContour from "../../components/panel-add-contour";
import RightsAware from '@/lib/mixins/rights-aware';
import { State, Getter, Mutation } from 'vuex-class';
import XInput from '@/components/x-input';
import CavCavite from '@/lib/models/cav-cavite';
import BeanCoord from '@/lib/models/bean-coord';
import ComSystProjectFrance from '@/lib/models/com-syst-project-france';
import { LcavNatureType, LcavStatut, LcavTypeReperage, LcavTypecav, LcavTypePositionnement, LcavSourceCoordonnees } from '@/lib/models/gen-lexs';
import ControlCavDocuments from '../../components/control-documents';
import User from '@/lib/models/user';

@Component
export default class PartDescription extends Mixins(DynPanelAware, RightsAware) {

  @Getter getLexValue;
  @Getter getLexique;
  @Getter getPositionableBeanCoord;
  @Getter loggedUser: User;
  @Mutation updateGeometryAction;

  @State(s => s.caviteEdit.editedCavite) data: CavCavite;
  @State(s => s.caviteEdit.editedCaviteGeometrieAction) geometrieAction: {what: string; file?: string};

  get lexLcavNatureTypeComputed() {
    return this.getLexique(LcavNatureType)
      .filter(({typeCavite}) => this.data.typeCavite === typeCavite);
  }

  get lexLcavStatutTypeComputed() {
    return this.getLexique(LcavStatut)
      .filter(({lcavTypecav}) => this.data.typeCavite === lcavTypecav);
  }

  get lexLcavTypeReperage() {
    return this.getLexique(LcavTypeReperage)
      .filter(({lcavNatureType}) => this.data.natureCavite === lcavNatureType);
  }

  get currentCavBeanCoord() {
    return this.getPositionableBeanCoord(this.data);
  }

  get hasGeometrie() {
    return (this.data.idGeometrie && this.geometrieAction?.what !== "delete")
      || this.geometrieAction?.what === "save";
  }

  @Watch("data.typeCavite")
  onTypeCavChange() {
    if (!this.lexLcavNatureTypeComputed.some(({typeCavite}) => typeCavite === this.data.typeCavite)) {
      this.data.natureCavite = null;
    }
    if (!this.lexLcavStatutTypeComputed.some(({lcavTypecav}) => lcavTypecav === this.data.typeCavite)) {
      this.data.statut = null;
    }
  }

  @Watch("data.natureCavite")
  onNatureCavChange() {
    if (!this.lexLcavTypeReperage.some(({lcavNatureType}) => lcavNatureType === this.data.natureCavite)) {
      this.data.reperageGeographique = null;
    }
  }

  @Watch("data.xouvrage")
  @Watch("data.youvrage")
  @Watch("data.lambertOuvrage")
  onLambertOuvrageChange() {
    if (this.currentCavBeanCoord) {
      const beanL93 = this.currentCavBeanCoord.asL93();
      if (beanL93.isInFrance) {
        this.data.inject({
          xouv93: beanL93.x,
          youv93: beanL93.y
        });
      } else {
        this.data.inject({
          xouv93: null,
          youv93: null
        });
      }
    } else {
      this.data.inject({
        xouv93: null,
        youv93: null
      });
    }
  }


  mergeCoords(coords: BeanCoord) {
    const syst: ComSystProjectFrance = this.getLexValue(
      "ComSystProjectFrance",
      coords.epsg,
      l => l.epsg,
      (a, b) => a && b && a.toLowerCase() === b.toLowerCase()
    );
    this.data.xouvrage = coords.x;
    this.data.youvrage = coords.y;
    this.data.lambertOuvrage = syst.codeSystProjection;
  }

  onAddControur(data) {
    this.updateGeometryAction({
      what: "save",
      file: data
    });
  }

  onRemoveContour() {
    if (this.data.idGeometrie) {
      this.updateGeometryAction({what: "delete"});
    } else {
      this.updateGeometryAction(null);
    }
  }

  render() {
    if (!this.data) { return null; }
    return (
      <div class="main-tab-inner">
        <div class="columns">
          <div class="column is-4">
            <div class="is-fluid">
              <div class="is-flex is-airy">
                <div class="field is-fluid">
                  <label class="label is-small">Type (*)</label>
                  <p class="control">
                    <ListDropdown class="basic"
                      vModel={this.data.typeCavite}
                      data={this.getLexique(LcavTypecav)}
                      labelFn={v => v.famille}
                      transform={v => v.famille}
                      prompt="-"/>
                  </p>
                </div>
                <div class="field is-fluid">
                  <label class="label is-small">Nature (*)</label>
                  <p class="control">
                    <ListDropdown class="basic"
                      vModel={this.data.natureCavite}
                      data={this.lexLcavNatureTypeComputed}
                      labelFn={v => v.nature}
                      transform={v => v.nature}
                      disabled={!this.data.typeCavite}
                      prompt="-"/>
                  </p>
                </div>
              </div>
              {/* */}
              <div class="field">
                <label class="label is-small">Utilisation (*)</label>
                <p class="control">
                  <ListDropdown class="basic"
                    vModel={this.data.statut}
                    data={this.lexLcavStatutTypeComputed}
                    labelFn={v => v.statut}
                    transform={v => v.statut}
                    disabled={!this.data.typeCavite}
                    prompt="-"/>
                </p>
              </div>
              {/* */}
              <div class="field">
                <label class="label is-small">Type de repérage (*)</label>
                <p class="control">
                  <ListDropdown class="basic"
                    vModel={this.data.reperageGeographique}
                    data={this.lexLcavTypeReperage}
                    labelFn={v => v.libelleReperage}
                    transform={v => v.typeReperage}
                    disabled={!this.data.natureCavite}
                    prompt="-"/>
                </p>
              </div>
              {/* */}
              <div class="field">
                <label class="label is-small">Positionnement de la cavité (*)</label>
                <p class="control">
                  <ListDropdown class="basic"
                    vModel={this.data.positionnement}
                    data={this.getLexique(LcavTypePositionnement)}
                    labelFn={v => v.libellePosition}
                    transform={v => v.typePositionnement}
                    prompt="-"/>
                </p>
              </div>
              {/* */}
              <div class="field">
                <label class="label is-small">Source coord. (*)</label>
                <p class="control">
                  <ListDropdown class="basic"
                      vModel={this.data.sourceCoordonnees}
                      data={this.getLexique(LcavSourceCoordonnees)}
                      labelFn={v => v.sourceCoordonnees}
                      transform={v => v.sourceCoordonnees}
                      prompt="-"/>
                </p>
              </div>
              {/* */}
              <div class="field">
                <LabelWithHelp class="is-small has-text-weight-normal" withIcon={true}
                  message={$t("help.label.contour.fileType")} tooltipPosition="right">
                    Contour
                </LabelWithHelp>
                <p class="control">
                  {this.hasGeometrie ? (
                    <button class={`button is-small`} onClick={this.onRemoveContour}>
                      <Icon icon="delete-geom" class="icon is-medium"/>
                      <span>Supprimer le contour</span>
                    </button>
                  ) : (
                    <button class="button is-small" onClick={() => this.pushPanel({
                      clazz: PanelAddContour,
                      props: {
                        readonly: this.isReadonly,
                        isCus: false
                      },
                      on: {
                        commit: ({emitter, data}) => (this.onAddControur(data), emitter.remove())
                      }
                    })}>
                      <Icon icon="draw-geom" class="icon is-medium"/>
                      <span>Ajouter un contour</span>
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-6 is-offset-2">
            <div class="is-fluid">
              <div class="columns">
                <div class="column is-half">
                  <div class="field">
                    <LabelWithHelp class="is-small" withIcon={true}
                      message={$t("help.label.precision")} tooltipPosition="bottom">
                        Précision (en mètres) (*)
                    </LabelWithHelp>
                    <p class="control">
                      <XInput ofType="number" vModel={this.data.precisionXY} class="input is-small"/>
                    </p>
                  </div>
                </div>
              </div>
              <div class="columns">
                <div class="column is-half">
                  <div class="field">
                    <label class="label is-small">Système de coord. (*)</label>
                    <p class="control">
                      <ListDropdown class="basic"
                        vModel={this.data.lambertOuvrage}
                        data={this.getLexique(ComSystProjectFrance)}
                        labelFn={v => v.systemeProjection}
                        transform={v => v.codeSystProjection}
                        prompt="-"/>
                    </p>
                  </div>
                  <div class="columns">
                    <div class="column is-flex is-airy">
                      <div class="field is-fluid">
                        <label class="label is-small">X (*)</label>
                        <p class="control">
                          <XInput ofType="number" vModel={this.data.xouvrage} class="input is-small"/>
                        </p>
                      </div>
                      <div class="field is-fluid">
                        <label class="label is-small">Y (*)</label>
                        <p class="control">
                          <XInput ofType="number" vModel={this.data.youvrage} class="input is-small"/>
                        </p>
                      </div>
                      <div class="field is-fluid">
                        <label class="label is-small">Z</label>
                        <p class="control">
                          <XInput ofType="number" vModel={this.data.zouvrage}class="input is-small"/>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {this.currentCavBeanCoord && this.currentCavBeanCoord.isInFrance && [
                  <div class="column is-1 right-bordered"></div>,
                  <div class="column is-5 is-flex is-centered is-x-centered">
                    <div class="field">
                      <label class="label is-small has-text-centered" style="margin-top: 1.5rem">Lambert 93</label>
                      <p class="control has-text-centered">
                        <div class="columns">
                          <div class="column is-half has-text-centered">X</div>
                          <div class="column is-half has-text-centered">Y</div>
                        </div>
                        <div class="columns" style="margin-top: -2.5rem">
                          <div class="column is-half has-text-centered"><small>{this.data.xouv93Rounded || '-'}</small></div>
                          <div class="column is-half has-text-centered"><small>{this.data.youv93Rounded || '-'}</small></div>
                        </div>
                      </p>
                    </div>
                  </div>
                ]}
              </div>
              <div class="columns">
                <div class="column">
                  <div class="field">
                    <div class="control">
                      <button class="button is-small" onClick={() => this.pushPanel({
                        clazz: PanelLocMap,
                        props: {
                          readonly: this.isReadonly,
                          cavCoords: this.currentCavBeanCoord,
                          numCavite: this.data.numCavite,
                          commune: this.data.commune
                        },
                        on: {
                          commit: ({emitter, data}) => (this.mergeCoords(data), emitter.remove())
                        }
                      })}>
                        <Icon icon="map-marker" class="icon is-medium"/>
                        <span>Localiser sur carte</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field"> 
          <label class="label is-small">Documents</label>
          <ControlCavDocuments collection={this.data.documents} />
        </div>
      </div>
    );
  }

}
// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-d50787d6/description.tsx" });